<script setup lang="ts">
import { onMounted, computed, ref, watch, defineComponent, h } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'
import Bs2Title from '@/components/common/layout/Bs2Title.vue'
import { PlusIcon, AdjustmentsHorizontalIcon, ArrowDownTrayIcon, InformationCircleIcon, ShieldCheckIcon } from '@heroicons/vue/24/outline'
import { useOrderStore } from '@/stores/useOrderStore'
import { useRegulationStore } from '@/stores/useRegulationStore'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import { useBoostCoreUsersStore } from '@/stores/useBoostCoreUsersStore'
import { useMyTeamStore } from '@/stores/useMyTeamStore'
import { useOrderDerogationStore } from '@/stores/useOrderDerogationStore'
import CounterCard from '@/components/common/ui/CounterCard.vue'
import GridVue from '@/components/data-visualization/grids/GridVue.vue'
import { dateComparator, formatDate } from '@/utils/dateUtils'
import BadgeGrid from '@/components/data-visualization/grids/BadgeGrid.vue'
import OrderTypeCell from '@/components/data-visualization/grids/OrderTypeCell.vue'
import OrdersGridActions from '@/components/features/orders/OrdersGridActions.vue'
import RegulationStatusBadge from '@/components/features/regulations/RegulationStatusBadge.vue'
import RegulationDevisLinkCell from '@/components/features/regulations/RegulationDevisLinkCell.vue'
import RegulationClientLinkCell from '@/components/features/regulations/RegulationClientLinkCell.vue'
import RegulationSiretLinkCell from '@/components/features/regulations/RegulationSiretLinkCell.vue'
import RegulationsGridActions from '@/components/features/regulations/RegulationsGridActions.vue'
import OrderDevisLinkCell from '@/components/features/orders/OrderDevisLinkCell.vue'
import OrderClientLinkCell from '@/components/features/orders/OrderClientLinkCell.vue'
import OrderSiretLinkCell from '@/components/features/orders/OrderSiretLinkCell.vue'
import ColumnSelector from '@/components/data-visualization/grids/ColumnSelector.vue'
import FormFilterSelector from '@/components/features/orders/FormFilterSelector.vue'
import SelectInput from '@/components/common/selects/SelectInput.vue'
import TextInput from '@/components/common/inputs/TextInput.vue'
import type { ColumnConfig } from '@/components/data-visualization/grids/ColumnSelector.vue'
import type { FilterModel } from 'ag-grid-community'
import { printCurrency } from '@/utils/numbers'
import { storeToRefs } from 'pinia'
import type { ITextFilterParams, IDateFilterParams } from 'ag-grid-community'
import { ORDER_STATUS } from '@/constants/orderStatus'
import { REGULATION_STATUS } from '@/constants/regulationStatus'
import { useToast } from 'vue-toast-notification'

const $toast = useToast()
const router = useRouter()
let titleClickCount = 0
let titleClickTimer: ReturnType<typeof setTimeout> | null = null
function onTitleClick() {
  titleClickCount++
  if (titleClickTimer) clearTimeout(titleClickTimer)
  titleClickTimer = setTimeout(() => { titleClickCount = 0 }, 500)
  if (titleClickCount >= 3 && isSuperAdmin.value) router.push('/dashboard/commissions')
}
const route = useRoute()
const orderStore = useOrderStore()
const regulationStore = useRegulationStore()
const boostCoreStore = useBoostCorePermissionStore()
const usersStore = useBoostCoreUsersStore()
const myTeamStore = useMyTeamStore()
const derogationStore = useOrderDerogationStore()
const { statusOptions } = storeToRefs(orderStore)

const pendingDerogationsCount = computed(() => derogationStore.pendingCounts ?? 0)

const isSuperAdmin = computed(() => boostCoreStore.include('SUPER_ADMIN'))
const isProjectRead = computed(() => boostCoreStore.isProjectRead)
const isAdv = computed(() => boostCoreStore.isAdv && !isSuperAdmin.value)
const isAdvManager = computed(() => boostCoreStore.include('ADV_MANAGER'))
const isSalesManager = computed(() => boostCoreStore.include('SALES_MANAGER'))
const isSalesDirector = computed(() => boostCoreStore.include('SALES_DIRECTOR'))
const canSelectUser = computed(() => isSuperAdmin.value || isSalesManager.value || isSalesDirector.value)
const canAccessRegulations = computed(() => boostCoreStore.canAccessRegulations)
const canCreateRegulation = computed(() => boostCoreStore.can('ADV_WRITE') || boostCoreStore.can('SALES_READ'))
const canSeeCommercial = computed(() =>
  boostCoreStore.can('ADV_READ') ||
  boostCoreStore.include('SALES_MANAGER') ||
  boostCoreStore.include('SALES_DIRECTOR'),
)
const canExportOrders = computed(
  () =>
    boostCoreStore.include('SUPER_ADMIN') ||
    boostCoreStore.include('ADV_MANAGER') ||
    boostCoreStore.can('ADV_READ') ||
    boostCoreStore.include('SALES_MANAGER') ||
    isProjectRead.value,
)
const counterStatusOptions = computed(() => {
  let opts = [...statusOptions.value].sort((a, b) => a.order - b.order)
  if (isAdv.value && !isAdvManager.value) {
    opts = opts.filter((o) => o.value !== ORDER_STATUS.DRAFT)
  }
  // Le compteur « Envoyé Manager » n'a de sens que pour les BU avec une
  // préférence de validation manager configurée.
  if (!boostCoreStore.hasBuValidationManager) {
    opts = opts.filter((o) => o.value !== ORDER_STATUS.SENT_TO_BU_MANAGER)
  }
  const inProgress = opts.find((o) => o.value === ORDER_STATUS.IN_PROGRESS)
  const waitingIdx = opts.findIndex((o) => o.value === ORDER_STATUS.WAITING)
  if (inProgress && waitingIdx >= 0) {
    opts = opts.filter((o) => o.value !== ORDER_STATUS.IN_PROGRESS)
    opts.splice(waitingIdx + 1, 0, inProgress)
  }
  return opts
})

const regulationStatusCounts = computed(() => {
  let regs = regulationStore.regulations || []
  if (filterDateReception.value.length > 0) {
    regs = regs.filter((r: any) => isDateInAnyMonth(r.createdAt, filterDateReception.value))
  }
  if (filterValidatedAtFrom.value && filterValidatedAtTo.value) {
    const from = new Date(filterValidatedAtFrom.value)
    const to = endOfDay(filterValidatedAtTo.value)
    regs = regs.filter((r: any) => {
      if (!r.validatedAt) return false
      const d = new Date(r.validatedAt)
      return d >= from && d <= to
    })
  } else if (filterDateTraitement.value.length > 0) {
    regs = regs.filter((r: any) => r.validatedAt && isDateInAnyMonth(r.validatedAt, filterDateTraitement.value))
  }
  const map: Record<string, { count: number; sumMarge: number }> = {}
  for (const r of regs) {
    const s = r.status || 'WAITING'
    if (!map[s]) map[s] = { count: 0, sumMarge: 0 }
    map[s].count += 1
    map[s].sumMarge += Number(r.amount) || 0
  }
  return map
})

const mergedStatusCounts = computed(() => {
  const order = orderStore.orders?.statusCounts || {}
  const reg = regulationStatusCounts.value
  const merged: Record<string, { count: number; sumMarge: number }> = { ...order }
  for (const [status, data] of Object.entries(reg)) {
    if (!merged[status]) merged[status] = { count: 0, sumMarge: 0 }
    merged[status] = {
      count: (merged[status].count || 0) + data.count,
      sumMarge: (merged[status].sumMarge || 0) + data.sumMarge,
    }
  }
  return merged
})

const selectedAuthorUuid = ref<string | null>(null)

const displayUsers = computed(() =>
  isSuperAdmin.value ? usersStore.users : myTeamStore.all,
)
const userOptions = computed(() => {
  const active = displayUsers.value.filter((u) => u.status === 'ACTIVE')
  const opts = active.map((u) => ({
    id: u.uuid,
    value: u.uuid,
    label: usersStore.getUserLabel(u),
  }))
  if (boostCoreStore.user && !opts.some((o) => o.value === boostCoreStore.user?.uuid)) {
    opts.unshift({
      id: boostCoreStore.user.uuid,
      value: boostCoreStore.user.uuid,
      label: usersStore.getUserLabel(boostCoreStore.user as any),
    })
  }
  return opts
})

function getCurrentMonthValue() {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
}

const filters = ref<Record<string, any>>({})
const searchQuery = ref<string>('')
const selectedStatusFilter = ref<string | null>(null)
const filterDateReception = ref<string[]>([])
const filterDateTraitement = ref<string[]>([getCurrentMonthValue()])
const filterValidatedAtFrom = ref<string>('')
const filterValidatedAtTo = ref<string>('')
const pageSize = ref<number>(50)
const page = ref<number>(1)
const regulationPageSize = ref<number>(10)
const regulationPage = ref<number>(1)

const visibleFormCodes = ref<string[]>([])

const HIDE_CHILDREN_KEY = 'bs2-orders-hide-children'
const hideChildren = ref<boolean>(localStorage.getItem(HIDE_CHILDREN_KEY) !== 'false')

const formFilterOptions = computed(() => {
  const models = orderStore.orderModels || []
  return models.map((m: any) => ({
    code: m.code,
    label: m.template?.name || m.template?.title || m.name || m.code,
  }))
})

function onFormFilterChanged(codes: string[]) {
  visibleFormCodes.value = codes
}

function applyQueryParams() {
  const raw = route.query.status
  const queryStatus = Array.isArray(raw) ? raw[0] : raw
  if (queryStatus) {
    selectedStatusFilter.value = queryStatus
    filters.value = { ...filters.value, status: { filterType: 'text', filter: queryStatus, type: 'equals' } }
  }
  if (canSelectUser.value && boostCoreStore.user) {
    const queryAuthor = route.query.authorUuid as string | undefined
    selectedAuthorUuid.value = queryAuthor || boostCoreStore.user.uuid
  }
}

onMounted(async () => {
  await orderStore.ensureOrderModels()
  if (!boostCoreStore.user) await boostCoreStore.fetchMe()
  if (boostCoreStore.isManager) {
    derogationStore.fetchSubordinatesDerogations({ pageSize: 1, page: 1 })
  } else {
    derogationStore.fetchMyDerogations({ pageSize: 1, page: 1 })
  }
  if (canSelectUser.value && boostCoreStore.user) {
    const queryAuthor = route.query.authorUuid as string | undefined
    selectedAuthorUuid.value = queryAuthor || boostCoreStore.user.uuid
    if (isSuperAdmin.value) {
      if (usersStore.users.length === 0) await usersStore.fetchUsers()
    } else {
      if (!myTeamStore.isLoaded) await myTeamStore.fetch()
    }
  }
  applyQueryParams()
  await fetchOrders()
  if (canAccessRegulations.value) await fetchRegulations()
})

watch(() => route.query.status, (newStatus) => {
  const statusVal = Array.isArray(newStatus) ? newStatus[0] : newStatus
  if (statusVal) {
    selectedStatusFilter.value = statusVal
    filters.value = { ...filters.value, status: { filterType: 'text', filter: statusVal, type: 'equals' } }
    page.value = 1
    regulationPage.value = 1
    fetchOrders()
    if (canAccessRegulations.value) fetchRegulations()
  }
})

watch(
  () => boostCoreStore.user,
  (user) => {
    if (canSelectUser.value && user && selectedAuthorUuid.value === null) {
      selectedAuthorUuid.value = user.uuid
    }
  },
  { immediate: true },
)

watch(canSelectUser, async (val) => {
  if (val && boostCoreStore.user) {
    const queryAuthor = route.query.authorUuid as string | undefined
    selectedAuthorUuid.value = queryAuthor || boostCoreStore.user.uuid
    if (isSuperAdmin.value) {
      if (usersStore.users.length === 0) await usersStore.fetchUsers()
    } else {
      if (!myTeamStore.isLoaded) await myTeamStore.fetch()
    }
  }
})

watch(selectedAuthorUuid, () => {
  page.value = 1
  regulationPage.value = 1
  fetchOrders()
  if (canAccessRegulations.value) fetchRegulations()
}, { immediate: false })
watch(searchQuery, () => {
  page.value = 1
  regulationPage.value = 1
  if (timeout) clearTimeout(timeout)
  timeout = setTimeout(() => {
    fetchOrders()
    if (canAccessRegulations.value) fetchRegulations()
  }, 500)
}, { immediate: false })
watch([filterDateReception, filterDateTraitement], () => {
  page.value = 1
  regulationPage.value = 1
  fetchOrders()
  if (canAccessRegulations.value) fetchRegulations()
}, { immediate: false, deep: true })
watch([filterValidatedAtFrom, filterValidatedAtTo], () => {
  page.value = 1
  regulationPage.value = 1
  fetchOrders()
  if (canAccessRegulations.value) fetchRegulations()
}, { immediate: false })
watch(visibleFormCodes, () => {
  page.value = 1
  fetchOrders()
  fetchRegulations()
}, { deep: true })
watch(hideChildren, (val) => {
  localStorage.setItem(HIDE_CHILDREN_KEY, String(val))
  page.value = 1
  fetchOrders()
})


/**
 * Filtre AG Grid pour grille server-paginée : capture la saisie "contient" et la
 * remonte (getModel → onFilterChanged → fetch serveur), mais ne filtre JAMAIS les
 * lignes côté client (doesFilterPass = true). Le serveur fait le filtrage ; sans ça,
 * AG masquerait les lignes correctes renvoyées par le backend.
 */
class ServerSideContainsFilter {
  private value = ''
  private gui!: HTMLDivElement
  private input!: HTMLInputElement
  private params: any
  init(params: any) {
    this.params = params
    this.gui = document.createElement('div')
    this.gui.className = 'p-2'
    this.input = document.createElement('input')
    this.input.type = 'text'
    this.input.className = 'input input-bordered input-sm w-full'
    this.input.placeholder = 'Contient…'
    this.input.addEventListener('input', () => {
      this.value = this.input.value.trim()
      this.params.filterChangedCallback()
    })
    this.gui.appendChild(this.input)
  }
  getGui() { return this.gui }
  isFilterActive() { return this.value.length > 0 }
  doesFilterPass() { return true }
  getModel() { return this.value ? { filterType: 'text', type: 'contains', filter: this.value } : null }
  setModel(model: any) {
    this.value = model?.filter ?? ''
    if (this.input) this.input.value = this.value
  }
}

const allColumns = computed<ColumnConfig[]>(() => [
  {
    field: 'uuid',
    headerName: '#',
    visible: false,
    locked: true,
  },
  {
    field: 'quoteReference',
    headerName: 'N° Devis',
    visible: true,
  },
  {
    field: 'salesEngineer',
    headerName: 'Commercial',
    visible: canSeeCommercial.value,
    locked: !canSeeCommercial.value,
  },
  {
    field: 'adv',
    headerName: 'ADV',
    visible: true,
  },
  {
    field: 'orderTemplate',
    headerName: 'Formulaire',
    visible: true,
  },
  {
    field: 'orderType',
    headerName: 'Type',
    visible: true,
  },
  {
    field: 'company',
    headerName: 'Client',
    visible: true,
  },
  {
    field: 'siret',
    headerName: 'SIRET',
    visible: true,
  },

  {
    field: 'marge',
    headerName: 'Marge Compteur',
    visible: true,
  },
  {
    field: 'createdAt',
    headerName: 'Date de création',
    visible: true,
  },
  {
    field: 'updatedAt',
    headerName: 'Date de modification',
    visible: true,
  },
  {
    field: 'status',
    headerName: 'Statut',
    visible: true,
  },
  {
    field: 'actions',
    headerName: 'Actions',
    visible: true,
    locked: true, // Ne peut pas être caché
  },
])
const visibleColumnFields = ref<string[]>([])

let timeout: any = null

function effectiveOrderMargeCompteur(o: { forceMarge?: number | null; margeCompteur?: number | null } | null | undefined) {
  if (!o) return null
  if (o.forceMarge != null) {
    const n = Number(o.forceMarge)
    return Number.isFinite(n) ? n : null
  }
  if (o.margeCompteur != null) {
    const n = Number(o.margeCompteur)
    return Number.isFinite(n) ? n : null
  }
  return null
}

const MargeCell = defineComponent({
  props: ['params'],
  setup(props) {
    return () => {
      const v = effectiveOrderMargeCompteur(props.params?.data)
      let text: string
      if (v == null) text = '-'
      else {
        const n = typeof v === 'number' ? v : Number(v)
        text = isNaN(n) ? '-' : printCurrency(n)
      }
      if (props.params?.data?.forceMarge != null) {
        return h('span', { class: 'flex items-center gap-1' }, [
          text,
          h('span', { title: 'Cette marge a été forcée à la main', style: 'cursor: help; display: inline-flex;' }, [
            h(InformationCircleIcon, { class: 'w-4 h-4 shrink-0 text-orange-500' }),
          ]),
        ])
      }
      return h('span', text)
    }
  },
})

const gridColumns = computed(() => {
  const visibleFields = new Set(visibleColumnFields.value)
  return [
    {
      field: 'quote.quoteReference',
      headerName: 'N° Devis',
      cellClass: 'border-0',
      cellRenderer: OrderDevisLinkCell,
      hide: !visibleFields.has('quoteReference'),
      filterParams: {
        filterOptions: ['contains'],
        maxNumConditions: 1,
      } as ITextFilterParams,
    },
    {
      field: 'quote.salesEngineer',
      headerName: 'Commercial',
      cellClass: 'border-0',
      hide: !visibleFields.has('salesEngineer') || !canSeeCommercial.value,
      valueFormatter: (params: any) => {
        const se = params.data?.quote?.salesEngineer
        if (!se) return '-'
        return [se.firstName, se.lastName].filter(Boolean).join(' ') || '-'
      },
      // quote.salesEngineer est un objet, filtré côté serveur (prénom/nom) sans masquage client.
      filter: ServerSideContainsFilter,
    },
    {
      field: 'adv',
      headerName: 'ADV',
      cellClass: 'border-0',
      hide: !visibleFields.has('adv') ,
      valueFormatter: (params: any) => {
        const adv = params.data?.adv
        if (!adv) return '-'
        const name = [adv.firstName, adv.lastName].filter(Boolean).join(' ')
        return name || '-'
      },
      // adv est un objet, filtré côté serveur (prénom/nom) sans masquage client.
      filter: ServerSideContainsFilter,
    },
    {
      field: 'orderTemplate',
      headerName: 'Formulaire',
      cellClass: 'border-0',
      hide: !visibleFields.has('orderTemplate'),
      valueGetter: (params: any) => {
        const t = params.data?.orderTemplate
        if (!t) return ''
        return t.template?.name || t.template?.title || t.code || ''
      },
      valueFormatter: (params: any) => {
        const t = params.data?.orderTemplate
        if (!t) return '-'
        const name = t.template?.name || t.template?.title || t.code
        return name || t.code || '-'
      },
      // orderTemplate est filtré côté serveur : ce filtre capture la saisie sans
      // masquer les lignes renvoyées par le backend (cf. ServerSideContainsFilter).
      filter: ServerSideContainsFilter,
    },
    {
      field: 'quote.orderType',
      headerName: 'Type',
      cellClass: 'border-0 !items-start',
      cellRenderer: OrderTypeCell,
      hide: !visibleFields.has('orderType'),
      filter: false,
    },
    {
      field: 'quote.company.name',
      headerName: 'Client',
      cellClass: 'border-0',
      cellRenderer: OrderClientLinkCell,
      hide: !visibleFields.has('company'),
      filterParams: {
        filterOptions: ['contains'],
        maxNumConditions: 1,
      } as ITextFilterParams,
    },
    {
      field: 'quote.company.siret',
      headerName: 'SIRET',
      cellClass: 'border-0',
      cellRenderer: OrderSiretLinkCell,
      hide: !visibleFields.has('siret'),
      filterParams: {
        filterOptions: ['contains'],
        maxNumConditions: 1,
      } as ITextFilterParams,
    },

    {
      field: 'margeCompteur',
      headerName: 'Marge Compteur',
      cellClass: 'border-0',
      filter: false,
      hide: !visibleFields.has('marge'),
      valueGetter: (params: any) => effectiveOrderMargeCompteur(params.data),
      cellRenderer: MargeCell,
      comparator: (a: any, b: any) => {
        const n1 = a != null ? Number(a) : NaN
        const n2 = b != null ? Number(b) : NaN
        if (isNaN(n1) && isNaN(n2)) return 0
        if (isNaN(n1)) return -1
        if (isNaN(n2)) return 1
        return n1 - n2
      },
    },
    {
      field: 'createdAt',
      headerName: 'Date de création',
      cellClass: 'border-0',
      filter: 'agDateColumnFilter',
      valueFormatter: (params: any) => formatDate(params.value?.toString() || ''),
      comparator: dateComparator,
      hide: !visibleFields.has('createdAt'),
      filterParams: {
        filterOptions: ['equals', 'inRange', 'lessThan', 'greaterThan'],
        maxNumConditions: 1,
      } as IDateFilterParams,
    },
    {
      field: 'updatedAt',
      headerName: 'Date de modification',
      cellClass: 'border-0',
      valueFormatter: (params: any) => formatDate(params.value?.toString() || ''),
      filter: 'agDateColumnFilter',
      comparator: dateComparator,
      hide: !visibleFields.has('updatedAt'),
      filterParams: {
        filterOptions: ['equals', 'inRange', 'lessThan', 'greaterThan'],
        maxNumConditions: 1,
      } as IDateFilterParams,
    },
    {
      field: 'status',
      headerName: 'Statut',
      cellClass: 'border-0',
      cellRenderer: BadgeGrid,
      hide: !visibleFields.has('status'),
      // filter: SelectFilterGrid,
      filterParams: {
        filterOptions: ['contains'],

        maxNumConditions: 1,
      } as ITextFilterParams,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      resizable: false,
      filter: false,
      minWidth: 100,
      maxWidth: 100,
      cellRenderer: OrdersGridActions,
      sortable: false,
      cellClass: 'border-0 grid-action',
      hide: !visibleFields.has('actions'),
    },
  ]
})

const allColumnsRegulation: ColumnConfig[] = [
  { field: 'uuid', headerName: '#', visible: false },
  { field: 'quoteReference', headerName: 'N° Devis', visible: true },
  { field: 'commercial', headerName: 'Commercial', visible: true },
  { field: 'adv', headerName: 'ADV', visible: true },
  { field: 'family', headerName: 'Formulaire', visible: true },
  { field: 'company', headerName: 'Client', visible: true },
  { field: 'siret', headerName: 'SIRET', visible: true },
  { field: 'amount', headerName: 'Marge compteur', visible: true },
  { field: 'createdAt', headerName: 'Date création', visible: true },
  { field: 'validatedAt', headerName: 'Date validation', visible: true },
  { field: 'status', headerName: 'Statut', visible: true },
  { field: 'actions', headerName: 'Actions', visible: true, locked: true },
]

const defaultVisibleFieldsRegulation = ['quoteReference', 'commercial', 'adv', 'company', 'siret', 'amount', 'createdAt', 'validatedAt', 'status', 'actions']
const visibleColumnFieldsRegulation = ref<string[]>(defaultVisibleFieldsRegulation)

function getRegulationCommercialLabel(row: any) {
  const se = row?.salesEngineer
  if (!se) return '-'
  return [se.firstName, se.lastName].filter(Boolean).join(' ') || se.email || '-'
}

function getRegulationAdvLabel(row: any) {
  const adv = row?.adv
  if (!adv) return '-'
  return [adv.firstName, adv.lastName].filter(Boolean).join(' ') || adv.email || '-'
}

const gridColumnsRegulation = computed(() => {
  const visibleFields = new Set(visibleColumnFieldsRegulation.value)
  return [
    {
      field: 'quote.quoteReference',
      headerName: 'N° Devis',
      cellClass: 'border-0',
      cellRenderer: RegulationDevisLinkCell,
      hide: !visibleFields.has('quoteReference'),
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 } as ITextFilterParams,
    },
    {
      field: 'commercial',
      headerName: 'Commercial',
      cellClass: 'border-0',
      hide: !visibleFields.has('commercial'),
      valueGetter: (params: any) => getRegulationCommercialLabel(params.data),
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 } as ITextFilterParams,
    },
    {
      field: 'adv',
      headerName: 'ADV',
      cellClass: 'border-0',
      hide: !visibleFields.has('adv'),
      valueGetter: (params: any) => getRegulationAdvLabel(params.data),
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 } as ITextFilterParams,
    },
    {
      field: 'family',
      headerName: 'Formulaire',
      cellClass: 'border-0',
      hide: !visibleFields.has('family'),
      valueFormatter: (params: any) => {
        const f = params.data?.family
        const template = orderStore.orderModels?.find((m: any) => m.code === f)?.template

        if (!f) return '-'
        const name = template?.name || template?.title || template?.code
        return name || template?.code || '-'
      },
      filterParams: {
        filterOptions: ['contains'],
        maxNumConditions: 1,
      } as ITextFilterParams,
    },
    {
      field: 'quote.company.name',
      headerName: 'Client',
      cellClass: 'border-0',
      cellRenderer: RegulationClientLinkCell,
      hide: !visibleFields.has('company'),
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 } as ITextFilterParams,
    },
    {
      field: 'quote.company.siret',
      headerName: 'SIRET',
      cellClass: 'border-0',
      cellRenderer: RegulationSiretLinkCell,
      hide: !visibleFields.has('siret'),
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 } as ITextFilterParams,
    },
    {
      field: 'amount',
      headerName: 'Marge compteur',
      filter: false,
      cellClass: 'border-0',
      hide: !visibleFields.has('amount'),
      valueFormatter: (params: any) => {
        const v = params.value
        if (v == null || v === '') return '-'
        const n = typeof v === 'number' ? v : Number(v)
        return isNaN(n) ? '-' : new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n)
      },
      comparator: (a: any, b: any) => {
        const n1 = a != null ? Number(a) : NaN
        const n2 = b != null ? Number(b) : NaN
        if (isNaN(n1) && isNaN(n2)) return 0
        if (isNaN(n1)) return -1
        if (isNaN(n2)) return 1
        return n1 - n2
      },
    },
    {
      field: 'createdAt',
      headerName: 'Date création',
      cellClass: 'border-0',
      filter: 'agDateColumnFilter',
      valueFormatter: (params: any) => {
        const v = params.value
        if (v == null || v === false || v === '') return '-'
        const formatted = formatDate(v?.toString() || '')
        return formatted === 'false' ? '-' : formatted
      },
      comparator: dateComparator,
      hide: !visibleFields.has('createdAt'),
      filterParams: {
        filterOptions: ['equals', 'inRange', 'lessThan', 'greaterThan'],
        maxNumConditions: 1,
      } as IDateFilterParams,
    },
    {
      field: 'validatedAt',
      headerName: 'Date validation',
      cellClass: 'border-0',
      valueFormatter: (params: any) => {
        const v = params.value
        if (v == null || v === false || v === '') return '-'
        const formatted = formatDate(v?.toString() || '')
        return formatted === 'false' ? '-' : formatted
      },
      filter: 'agDateColumnFilter',
      comparator: dateComparator,
      hide: !visibleFields.has('validatedAt'),
      filterParams: {
        filterOptions: ['equals', 'inRange', 'lessThan', 'greaterThan'],
        maxNumConditions: 1,
      } as IDateFilterParams,
    },
    {
      field: 'status',
      headerName: 'Statut',
      cellClass: 'border-0',
      cellRenderer: RegulationStatusBadge,
      hide: !visibleFields.has('status'),
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 } as ITextFilterParams,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      resizable: false,
      filter: false,
      minWidth: 100,
      cellRenderer: RegulationsGridActions,
      sortable: false,
      cellClass: 'border-0 grid-action',
      hide: !visibleFields.has('actions'),
    },
  ]
})

function onRegulationColumnsChanged(newVisibleColumns: string[]) {
  visibleColumnFieldsRegulation.value = newVisibleColumns
}

watch(
  filters,
  () => {
    if (timeout) clearTimeout(timeout)
    // Un changement de filtre doit repartir de la page 1 (sinon on peut tomber
    // sur une page vide au-delà du nombre de résultats filtrés).
    page.value = 1
    timeout = setTimeout(() => {
      fetchOrders()
    }, 500)
  },
  { deep: true },
)

function onCreateOrder() {
  router.push('/dashboard/order/create')
}

function onCounterCardClick(statusValue: string) {
  if (selectedStatusFilter.value === statusValue) {
    selectedStatusFilter.value = null
    const { status: _s, ...rest } = filters.value
    filters.value = rest
  } else {
    selectedStatusFilter.value = statusValue
    filters.value = { ...filters.value, status: { filterType: 'text', filter: statusValue, type: 'equals' } }
  }
  page.value = 1
  regulationPage.value = 1
  fetchOrders()
}

const dateFilterOptions = computed(() => {
  const now = new Date()
  const opts: { id: string; value: string; label: string }[] = []
  for (let i = 0; i < 24; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    const label = d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
    opts.push({ id: val, value: val, label })
  }
  return opts
})

const gridFilterModel = computed(() => {
  const f = filters.value
  const model: Record<string, any> = {}
  if (f.status) model.status = f.status
  return Object.keys(model).length ? model : undefined
})

function endOfDay(dateStr: string): Date {
  const d = new Date(dateStr)
  d.setHours(23, 59, 59, 999)
  return d
}

function localYmd(d: Date): string {
  const y = d.getFullYear()
  const mo = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${mo}-${day}`
}

function validMonths(months: string[] | null | undefined): string[] {
  if (!Array.isArray(months)) return []
  return months.filter((m) => /^\d{4}-\d{2}$/.test(m))
}

function isDateInAnyMonth(dateStr: string | Date | null, months: string[] | null | undefined): boolean {
  const list = validMonths(months)
  if (list.length === 0) return true
  if (!dateStr) return false
  const d = typeof dateStr === 'string' ? new Date(dateStr) : dateStr
  const dy = d.getFullYear()
  const dm = d.getMonth() + 1
  return list.some((ym) => {
    const [y, m] = ym.split('-').map(Number)
    return dy === y && dm === m
  })
}

function monthsEnvelope(months: string[] | null | undefined): { dateFrom: string; dateTo: string } | undefined {
  const list = validMonths(months)
  if (list.length === 0) return undefined
  const sorted = list.slice().sort()
  const [yMin, mMin] = sorted[0].split('-').map(Number)
  const [yMax, mMax] = sorted[sorted.length - 1].split('-').map(Number)
  const start = new Date(yMin, mMin - 1, 1)
  const end = new Date(yMax, mMax, 0)
  return { dateFrom: localYmd(start), dateTo: localYmd(end) }
}

async function fetchRegulations() {
  const payload: { authorUuid?: string; search?: string; status?: string, orderTemplateCode?: string[] } = {}
  const authorUuid = selectedAuthorUuid.value ?? boostCoreStore.user?.uuid
  if (authorUuid) payload.authorUuid = authorUuid
  if (searchQuery.value) payload.search = searchQuery.value
  if (isAdv.value) {
    const allFormCodes = formFilterOptions.value.map((f) => f.code)
    const someFormsHidden = allFormCodes.length > 0 && visibleFormCodes.value.length < allFormCodes.length
    if (someFormsHidden) {
      if (visibleFormCodes.value.length === 0) {
        regulationStore.regulations = []
        return
      }
      payload.orderTemplateCode = visibleFormCodes.value
    }
  }
  await regulationStore.fetchRegulations(payload)
}

const filteredRegulationsForGrid = computed(() => {
  let list = regulationStore.regulations || []

  const status = selectedStatusFilter.value
  if (status) list = list.filter((r: any) => r.status === status)
  if (filterDateReception.value.length > 0) {
    list = list.filter((r: any) => isDateInAnyMonth(r.createdAt, filterDateReception.value))
  }
  // La date de traitement ne concerne que les réguls validées :
  // on ne l'applique que si l'utilisateur filtre explicitement sur "Validé", sinon on prend tout.
  if (status === REGULATION_STATUS.VALIDATED) {
    if (filterValidatedAtFrom.value && filterValidatedAtTo.value) {
      const from = new Date(filterValidatedAtFrom.value)
      const to = endOfDay(filterValidatedAtTo.value)
      list = list.filter((r: any) => {
        if (!r.validatedAt) return false
        const d = new Date(r.validatedAt)
        return d >= from && d <= to
      })
    } else if (filterDateTraitement.value.length > 0) {
      list = list.filter((r: any) => isDateInAnyMonth(r.validatedAt, filterDateTraitement.value))
    }
  }
  return list
})

const paginatedRegulationsForGrid = computed(() => {
  const list = filteredRegulationsForGrid.value
  const start = (regulationPage.value - 1) * regulationPageSize.value
  return list.slice(start, start + regulationPageSize.value)
})

const regulationTotalPages = computed(() =>
  Math.max(1, Math.ceil(filteredRegulationsForGrid.value.length / regulationPageSize.value)),
)

watch(
  () => filteredRegulationsForGrid.value.length,
  () => {
    if (regulationPage.value > regulationTotalPages.value) {
      regulationPage.value = regulationTotalPages.value
    }
  },
)

function onRegulationPaginationChanged(newPageSize: number, newPage: number) {
  regulationPageSize.value = newPageSize
  regulationPage.value = newPage
}

async function fetchOrders() {
  const payload = { ...filters.value }
  const authorUuid = selectedAuthorUuid.value ?? boostCoreStore.user?.uuid

  if (searchQuery.value) {
    payload.search = searchQuery.value
  }

  const statusFilter = payload.status?.filter || payload.status?.value
  const isDraftStatus = statusFilter === ORDER_STATUS.DRAFT

  if (authorUuid && (!isAdv.value || (isDraftStatus && isAdv.value && !isAdvManager.value))) {
    payload.authorUuid = authorUuid
  }

  if (isAdv.value) {
    const allFormCodes = formFilterOptions.value.map((f) => f.code)
    const someFormsHidden = allFormCodes.length > 0 && visibleFormCodes.value.length < allFormCodes.length
    if (someFormsHidden) {
      if (visibleFormCodes.value.length === 0) {
        orderStore.orders = { data: [], page: 1, pageSize: pageSize.value, total: 0, totalPages: 0, statusCounts: {} }
        orderStore.orderUuids = []
        return
      }
      payload.orderTemplateCode = visibleFormCodes.value
    }
    if (hideChildren.value) {
      payload.hideChildren = true
    }
  }

  const sentAtMonths = validMonths(filterDateReception.value)
  if (sentAtMonths.length > 0) payload.sentAtMonths = sentAtMonths
  if (filterValidatedAtFrom.value && filterValidatedAtTo.value) {
    payload.validatedAt = { filterType: 'date', type: 'inRange', dateFrom: filterValidatedAtFrom.value, dateTo: filterValidatedAtTo.value }
  } else {
    const validatedAtMonths = validMonths(filterDateTraitement.value)
    if (validatedAtMonths.length > 0) payload.validatedAtMonths = validatedAtMonths
  }

  await orderStore.fetchOrders(payload, pageSize.value, page.value)
  orderStore.orderUuids = (orderStore.orders?.data || []).map((o: any) => o.uuid).filter(Boolean)
}

function onColumnsChanged(newVisibleColumns: string[]) {
  visibleColumnFields.value = newVisibleColumns
}

async function updateFilter(filtersModel: FilterModel) {
  if (filtersModel.status?.value) {
    filtersModel.status = { filterType: 'text', filter: filtersModel.status.value.value, type: 'equals' }
  }
  const statusVal = filtersModel.status?.filter ?? filtersModel.status?.value?.value
  selectedStatusFilter.value = statusVal ?? null
  filters.value = { ...filtersModel }
}

async function onPaginationChanged(newPageSize: number, newPage: number) {
  if (pageSize.value === newPageSize && page.value === newPage) return
  let changed = false
  if (pageSize.value !== newPageSize) {
    pageSize.value = newPageSize
    changed = true
  }
  if (page.value !== newPage) {
    page.value = newPage
    changed = true
  }

  if (changed) await fetchOrders()
}

const isExportingOrders = ref(false)
const isExportingCsv = ref(false)

function stripHtml(html: string): string {
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&nbsp;/g, ' ')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim()
}

function escapeCsvCell(val: unknown): string {
  if (val == null || val === '') return ''
  const s = String(val).replace(/"/g, '""')
  return s.includes(';') || s.includes('"') || s.includes('\n') || s.includes('\r') ? `"${s}"` : s
}

function formatAmountForCsv(n: number | null | undefined): string {
  if (n == null || (typeof n !== 'number' && isNaN(Number(n)))) return ''
  const num = typeof n === 'number' ? n : Number(n)
  return isNaN(num) ? '' : num.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: false })
}

function formatSiretForExcel(siret: string | null | undefined): string {
  if (siret == null || String(siret).trim() === '') return ''
  const s = String(siret).trim()
  return `="` + s.replace(/"/g, '""') + `"`
}

const REGULATION_STATUS_LABELS: Record<string, string> = {
  [REGULATION_STATUS.WAITING]: 'En attente',
  [REGULATION_STATUS.VALIDATED]: 'Validée',
  [REGULATION_STATUS.CANCELED]: 'Annulée',
}

function getRegulationStatusLabel(s: string): string {
  return REGULATION_STATUS_LABELS[s] ?? s ?? ''
}

async function onExportOrders() {
  if (isExportingOrders.value) return
  if (isAdv.value) {
    const allFormCodes = formFilterOptions.value.map((f) => f.code)
    if (allFormCodes.length > 0 && visibleFormCodes.value.length === 0) {
      $toast.warning('Aucune donnée à exporter')
      return
    }
  }
  isExportingOrders.value = true
  try {
    const payload = { ...filters.value }
    const authorUuid = selectedAuthorUuid.value ?? boostCoreStore.user?.uuid
    const statusFilter = payload.status?.filter || payload.status?.value
    const isDraftStatus = statusFilter === ORDER_STATUS.DRAFT
    if (authorUuid && (!(isAdv.value || isProjectRead.value) || (isDraftStatus && isAdv.value && !isAdvManager.value))) payload.authorUuid = authorUuid
    if (searchQuery.value) payload.search = searchQuery.value
    if (isAdv.value) {
      const allFormCodes = formFilterOptions.value.map((f) => f.code)
      const someFormsHidden = allFormCodes.length > 0 && visibleFormCodes.value.length < allFormCodes.length
      if (someFormsHidden) payload.orderTemplateCode = visibleFormCodes.value
    }
    const sentAtMonthsList = validMonths(filterDateReception.value)
    if (sentAtMonthsList.length > 0) payload.sentAtMonths = sentAtMonthsList
    if (filterValidatedAtFrom.value && filterValidatedAtTo.value) {
      payload.validatedAt = { filterType: 'date', type: 'inRange', dateFrom: filterValidatedAtFrom.value, dateTo: filterValidatedAtTo.value }
    } else {
      const validatedAtMonthsList = validMonths(filterDateTraitement.value)
      if (validatedAtMonthsList.length > 0) payload.validatedAtMonths = validatedAtMonthsList
    }

    const regulationPayload: { authorUuid?: string; search?: string; orderTemplateCode?: string[] } = {}
    if (authorUuid) regulationPayload.authorUuid = authorUuid
    if (searchQuery.value) regulationPayload.search = searchQuery.value
    if (isAdv.value) {
      const allFormCodes = formFilterOptions.value.map((f) => f.code)
      const someFormsHidden = allFormCodes.length > 0 && visibleFormCodes.value.length < allFormCodes.length
      if (someFormsHidden) regulationPayload.orderTemplateCode = visibleFormCodes.value
    }

    const [ordersRes, regulationsData] = await Promise.all([
      orderStore.fetchOrders(payload, 50000, 1),
      canAccessRegulations.value
        ? regulationStore.fetchRegulations(regulationPayload)
        : Promise.resolve([]),
    ])
    const orders = Array.isArray((ordersRes as any)?.data) ? (ordersRes as any).data : (orderStore.orders?.data ?? [])
    let regulations: any[] = Array.isArray(regulationsData) ? regulationsData : []
    if (statusFilter) regulations = regulations.filter((r: any) => r.status === statusFilter)
    if (filterDateReception.value.length > 0) {
      regulations = regulations.filter((r: any) => isDateInAnyMonth(r.createdAt, filterDateReception.value))
    }
    if (filterValidatedAtFrom.value && filterValidatedAtTo.value) {
      const from = new Date(filterValidatedAtFrom.value)
      const to = endOfDay(filterValidatedAtTo.value)
      regulations = regulations.filter((r: any) => {
        if (!r.validatedAt) return false
        const d = new Date(r.validatedAt)
        return d >= from && d <= to
      })
    } else if (filterDateTraitement.value.length > 0) {
      regulations = regulations.filter((r: any) => r.validatedAt && isDateInAnyMonth(r.validatedAt, filterDateTraitement.value))
    }

    const orderRows = orders.map((o: any) => {
      const se = o?.quote?.salesEngineer
      const adv = o?.adv
      const t = o?.orderTemplate
      const company = o?.quote?.company
      const orderType = o?.quote?.orderType
      const dur = o?.quote?.subscriptionDuration
      const typeLabel = orderType ? (dur ? `${orderType} ${dur?.label || dur?.code || ''}`.trim() : orderType) : ''
      const seLabel = se ? [se.firstName, se.lastName].filter(Boolean).join(' ') || '-' : '-'
      const advLabel = adv ? [adv.firstName, adv.lastName].filter(Boolean).join(' ') || '-' : '-'
      const margeTotal = effectiveOrderMargeCompteur(o) ?? 0
      const isRecurrent = orderType === 'SUBSCRIPTION'
      const margeRec = isRecurrent ? margeTotal : 0
      const margeOneShot = isRecurrent ? 0 : margeTotal
      return [
        escapeCsvCell('Bon de commande'),
        escapeCsvCell(o?.quote?.quoteReference ?? ''),
        escapeCsvCell(seLabel),
        escapeCsvCell(advLabel),
        escapeCsvCell(t?.template?.name ?? t?.template?.title ?? t?.code ?? ''),
        escapeCsvCell(typeLabel),
        escapeCsvCell(company?.name ?? ''),
        formatSiretForExcel(company?.siret ?? ''),
        formatAmountForCsv(margeRec),
        formatAmountForCsv(margeOneShot),
        formatAmountForCsv(margeTotal),
        escapeCsvCell(formatDate(o?.createdAt?.toString?.() ?? '')),
        escapeCsvCell(formatDate(o?.updatedAt?.toString?.() ?? '')),
        escapeCsvCell(orderStore.getStatusLabel(o?.status ?? '')),
        escapeCsvCell((o?.orderComments ?? []).map((c: any) => stripHtml(c.comment)).join(' ; ')),
        escapeCsvCell((o?.operatorComments ?? []).filter((c: any) => !c.deleted).map((c: any) => stripHtml(c.comment)).join(' ; ')),
        escapeCsvCell(stripHtml((o?.operatorComments ?? []).find((c: any) => c.favorite)?.comment ?? '')),
      ]
    })

    const regulationRows = regulations.map((r: any) => {
      const se = r?.salesEngineer ?? r?.quote?.salesEngineer
      const adv = r?.adv
      const company = r?.quote?.company ?? r?.company
      const seLabel = se ? [se.firstName, se.lastName].filter(Boolean).join(' ') || '-' : '-'
      const advLabel = adv ? [adv.firstName, adv.lastName].filter(Boolean).join(' ') || '-' : '-'
      const regOrderType = r?.quote?.orderType
      const amount = r?.amount ?? 0
      const isRecurrent = regOrderType === 'SUBSCRIPTION'
      const margeRec = isRecurrent ? amount : 0
      const margeOneShot = isRecurrent ? 0 : amount
      return [
        escapeCsvCell('Régularisation'),
        escapeCsvCell(r?.quote?.quoteReference ?? r?.quoteReference ?? ''),
        escapeCsvCell(seLabel),
        escapeCsvCell(advLabel),
        escapeCsvCell('-'),
        escapeCsvCell('-'),
        escapeCsvCell(company?.name ?? ''),
        formatSiretForExcel(company?.siret ?? ''),
        formatAmountForCsv(margeRec),
        formatAmountForCsv(margeOneShot),
        formatAmountForCsv(amount),
        escapeCsvCell(formatDate(r?.createdAt?.toString?.() ?? '')),
        escapeCsvCell(formatDate(r?.validatedAt?.toString?.() ?? '')),
        escapeCsvCell(getRegulationStatusLabel(r?.status ?? '')),
        escapeCsvCell(''),
        escapeCsvCell(''),
        escapeCsvCell(''),
      ]
    })

    const rows = [...orderRows, ...regulationRows]
    if (!rows.length) {
      $toast.warning('Aucune donnée à exporter')
      return
    }

    const headers = [
      'Type',
      'N° Devis',
      'Commercial',
      'ADV',
      'Formulaire',
      'Type contrat',
      'Client',
      'SIRET',
      'Marge Compteur Récurrent',
      'Marge Compteur OneShot',
      'Marge Compteur Total',
      'Date de création',
      'Date de modification',
      'Statut',
      'Commentaires',
      'Commentaires Opérateur',
      'Commentaire favori',
    ]
    const sep = ';'
    const csvLines = [headers.join(sep), ...rows.map((r) => r.join(sep))]
    const csv = '\uFEFF' + csvLines.join('\r\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `commandes_export_${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(a.href)
    $toast.success(`Export de ${rows.length} ligne(s) terminé`)
  } catch (e) {
    console.error(e)
    $toast.error("Erreur lors de l'export")
  } finally {
    isExportingOrders.value = false
  }
}

async function onExportCsv() {
  if (isExportingCsv.value) return

  // Validation : si une seule des deux dates custom est remplie
  if ((filterValidatedAtFrom.value && !filterValidatedAtTo.value) || (!filterValidatedAtFrom.value && filterValidatedAtTo.value)) {
    $toast.warning('Veuillez renseigner les deux dates de la plage d\'export')
    return
  }

  if (isAdv.value) {
    const allFormCodes = formFilterOptions.value.map((f) => f.code)
    if (allFormCodes.length > 0 && visibleFormCodes.value.length === 0) {
      $toast.warning('Aucune donnée à exporter')
      return
    }
  }

  isExportingCsv.value = true
  try {
    let dateFromStr: string
    let dateToStr: string

    const traitementEnvelope = monthsEnvelope(filterDateTraitement.value)
    if (filterValidatedAtFrom.value && filterValidatedAtTo.value) {
      // Priorité 1 : plage custom ADV
      dateFromStr = filterValidatedAtFrom.value
      dateToStr = filterValidatedAtTo.value
    } else if (traitementEnvelope) {
      // Priorité 2 : filtre mensuel (enveloppe min/max des mois sélectionnés)
      dateFromStr = traitementEnvelope.dateFrom
      dateToStr = traitementEnvelope.dateTo
    } else {
      // Priorité 3 : YTD (1er janvier → aujourd'hui)
      const now = new Date()
      dateFromStr = `${now.getFullYear()}-01-01`
      dateToStr = localYmd(now)
    }

    // Reconstruction des filtres actifs du tableau
    const exportFilters: {
      authorUuid?: string
      search?: string
      status?: string
      orderTemplateCode?: string[]
      sentAtFrom?: string
      sentAtTo?: string
      hideChildren?: boolean
    } = {}

    const authorUuid = selectedAuthorUuid.value ?? boostCoreStore.user?.uuid
    const statusFilter = filters.value.status?.filter || filters.value.status?.value
    const isDraftStatus = statusFilter === ORDER_STATUS.DRAFT
    if (authorUuid && (!(isAdv.value || isProjectRead.value) || (isDraftStatus && isAdv.value && !isAdvManager.value))) {
      exportFilters.authorUuid = authorUuid
    }
    if (searchQuery.value) exportFilters.search = searchQuery.value
    if (statusFilter) exportFilters.status = statusFilter
    if (isAdv.value) {
      const allFormCodes = formFilterOptions.value.map((f) => f.code)
      const someFormsHidden = allFormCodes.length > 0 && visibleFormCodes.value.length < allFormCodes.length
      if (someFormsHidden) exportFilters.orderTemplateCode = visibleFormCodes.value
      if (hideChildren.value) exportFilters.hideChildren = true
    }
    const receptionEnvelope = monthsEnvelope(filterDateReception.value)
    if (receptionEnvelope) {
      exportFilters.sentAtFrom = receptionEnvelope.dateFrom
      exportFilters.sentAtTo = receptionEnvelope.dateTo
    }

    const success = await orderStore.exportOrdersCsv(dateFromStr, dateToStr, exportFilters)

    if (success) {
      $toast.success('Export CSV terminé')
    } else {
      $toast.error("Erreur lors de l'export CSV")
    }
  } catch (e) {
    console.error(e)
    $toast.error("Erreur lors de l'export CSV")
  } finally {
    isExportingCsv.value = false
  }

}
async function goToDerogations() {
  await router.push('/dashboard/derogations')
}
</script>

<template>
  <div class="p-4">
    <div class="flex items-center justify-between gap-4">
      <Bs2Title
        v-if="isSuperAdmin"
        title="Tableau de bord des commandes"
        subtitle="Gérez vos commandes et créez-en de nouvelles"
        add-classes="cursor-default select-none"
        @click="onTitleClick"
      />
      <Bs2Title v-else title="Tableau de bord des commandes" subtitle="Gérez vos commandes et créez-en de nouvelles" />
      <div class="flex gap-2">
        <div v-if="!isProjectRead" class="relative">
          <ButtonDaisy
            text="Voir les dérogations"
            variant="secondary"
            size="sm"
            :icon="ShieldCheckIcon"
            @click="goToDerogations"
          />
          <span
            v-if="pendingDerogationsCount > 0"
            class="absolute -top-1.5 -right-1.5 badge badge-error badge-sm min-w-[1.25rem] text-white z-10 pointer-events-none"
          >{{ pendingDerogationsCount }}</span>
        </div>
        <ButtonDaisy
          v-if="isAdvManager || isSuperAdmin || isProjectRead"
          text="Export CSV"
          variant="secondary"
          :icon="ArrowDownTrayIcon"
          :disabled="isExportingCsv"
          @click="onExportCsv"
        />
        <ButtonDaisy
          v-if="canAccessRegulations"
          text="Créer une régularisation"
          variant="secondary"
          :icon="AdjustmentsHorizontalIcon"
          :disabled="!canCreateRegulation"
          @click="router.push('/dashboard/regulation/create')"
        />
        <ButtonDaisy
          v-if="!isProjectRead"
          text="Créer une commande"
          variant="primary"
          :icon="PlusIcon"
          @click="onCreateOrder"
        />
      </div>
    </div>

    <div class="mt-6 flex flex-nowrap gap-4 overflow-x-auto py-4">
      <CounterCard
        v-for="status in counterStatusOptions"
        :key="status.value"
        :counter="status"
        :status-counts="mergedStatusCounts"
        :class="['flex-1 min-w-[100px] shrink-0 self-stretch my-1', { 'ring-2 ring-primary ring-offset-2': selectedStatusFilter === status.value }]"
        @click="onCounterCardClick(status.value)"
      />
    </div>

    <div class="mt-6 card bg-base-100 shadow">
      <div class="card-body p-4">
        <div class="flex flex-row flex-wrap items-end gap-2 justify-end">
          <TextInput
            v-model="searchQuery"
            label="Recherche"
            placeholder="N° devis ou client"
            class="flex-1 min-w-[200px] max-w-full sm:max-w-[300px]"
          />
          <SelectInput
            v-model="filterDateReception"
            label="Date réception"
            :options="dateFilterOptions"
            placeholder="Toutes"
            multiple
            class="flex-1 min-w-[200px] max-w-full sm:max-w-[260px]"
          />
          <SelectInput
            v-model="filterDateTraitement"
            label="Date traitement"
            :options="dateFilterOptions"
            placeholder="Toutes"
            multiple
            class="flex-1 min-w-[200px] max-w-full sm:max-w-[260px]"
          />
          <label v-if="isAdvManager || isSuperAdmin" class="form-control w-full flex-1 min-w-[150px] max-w-full sm:max-w-[200px]">
            <div class="label">
              <span class="label-text font-semibold">Traitement du</span>
            </div>
            <input
              v-model="filterValidatedAtFrom"
              type="date"
              class="input input-bordered w-full h-[2.5rem] min-h-[2.5rem]"
            />
          </label>
          <label v-if="isAdvManager || isSuperAdmin" class="form-control w-full flex-1 min-w-[150px] max-w-full sm:max-w-[200px]">
            <div class="label">
              <span class="label-text font-semibold">Traitement au</span>
            </div>
            <input
              v-model="filterValidatedAtTo"
              type="date"
              class="input input-bordered w-full h-[2.5rem] min-h-[2.5rem]"
            />
          </label>
          <SelectInput
            v-if="canSelectUser"
            v-model="selectedAuthorUuid"
            label="Voir en tant que"
            :options="userOptions"
            placeholder="Sélectionner un commercial"
            class="flex-1 min-w-[200px] max-w-full sm:max-w-[250px]"
          />
          <FormFilterSelector
            v-if="isAdv"
            :forms="formFilterOptions"
            @form-filter-changed="onFormFilterChanged"
          />
          <label v-if="isAdv" class="flex items-center gap-2 cursor-pointer select-none text-sm">
            <input
              v-model="hideChildren"
              type="checkbox"
              class="checkbox checkbox-sm checkbox-primary"
            />
            <span>Masquer les commandes enfants</span>
          </label>
        </div>
      </div>
    </div>

    <div class="mt-6 card bg-base-100 shadow">
      <div class="card-body p-4 pt-2">
        <GridVue
          :list="orderStore.orders?.data || []"
          :cols="gridColumns"
          :filter-model="gridFilterModel"
          :get-row-height="(_params: any) => 48"
          :use-server-side-pagination="true"
          :pagination-page-size="orderStore.orders?.pageSize || 10"
          :pagination-total-rows="orderStore.orders?.total || 0"
          :current-page="orderStore.orders?.page || 1"
          :total-pages="orderStore.orders?.totalPages || 1"
          :show-export="false"
          @onFilterChanged="updateFilter"
          @onPaginationChanged="onPaginationChanged"
        >
          <template #title>
            <h2 class="card-title">Dernières commandes</h2>
          </template>
          <template #filter>
            <ColumnSelector :columns="allColumns" cookie-name="orders-columns-visibility" @columns-changed="onColumnsChanged" />
          </template>
          <template #button>
            <ButtonDaisy
              v-if="canExportOrders"
              :disabled="isExportingOrders"
              :text="isExportingOrders ? 'Export en cours...' : 'Export'"
              variant="secondary"
              :icon="ArrowDownTrayIcon"
              class="mb-1"
              @click="onExportOrders"
            />
          </template>
        </GridVue>
      </div>
    </div>

    <div v-if="canAccessRegulations" class="mt-6 card bg-base-100 shadow">
      <div class="card-body p-4 pt-2">
        <GridVue
          :list="paginatedRegulationsForGrid"
          :cols="gridColumnsRegulation"
          :get-row-height="(_params: any) => 48"
          :use-server-side-pagination="true"
          :pagination-page-size="regulationPageSize"
          :pagination-total-rows="filteredRegulationsForGrid.length"
          :current-page="regulationPage"
          :total-pages="regulationTotalPages"
          @onPaginationChanged="onRegulationPaginationChanged"
        >
          <template #title>
            <h2 class="card-title">Régularisations</h2>
          </template>
          <template #filter>
            <ColumnSelector :columns="allColumnsRegulation" cookie-name="regulations-columns-visibility" @columns-changed="onRegulationColumnsChanged" />
          </template>
        </GridVue>
      </div>
    </div>
  </div>
</template>
